import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CustomerReplies } from "../components/register/BespokeRegister";
import { toast } from "sonner";
import { Plus, Send, ExternalLink, Copy } from "lucide-react";
import { http, errMsg } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { RequestFields, BLANK_REQUEST } from "../components/bespoke/RequestFields";
import { BuildDialog } from "../components/bespoke/BuildDialog";
import { UploadBespoke } from "../components/bespoke/UploadBespoke";
import { EditRequest, DeleteRequest, ReviewPdfs, BriefVersions, PaymentSettings } from "../components/bespoke/RequestActions";

const STAGES = [["draft", "Draft", "bg-sky-100 text-sky-800"], ["review1", "Customer review (1)", "bg-violet-100 text-violet-800"], ["revision", "Revision", "bg-amber-100 text-amber-800"],
  ["review2", "Customer review (2)", "bg-violet-100 text-violet-800"], ["finalise", "Finalise", "bg-emerald-100 text-emerald-800"], ["complete", "Complete", "bg-stone-200 text-stone-700"], ["declined", "Declined", "bg-red-100 text-red-700"]];
const stageOf = (r) => STAGES.find(([k]) => k === (r.stage || "draft")) || STAGES[0];

const StageSelect = ({ r, reload }) => {
  const set = async (stage) => {
    try { await http.patch(`/bespoke/requests/${r.id}`, { stage }); toast.success(`Stage set to ${STAGES.find(([k]) => k === stage)[1]}`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const [, , cls] = stageOf(r);
  return (
    <select data-testid="request-stage-select" value={r.stage || "draft"} onChange={(e) => set(e.target.value)} title="Moves by itself as you send reviews and the customer replies; change it here to override"
      className={`rounded-full border-0 px-3 py-1 text-xs font-bold uppercase ${cls}`}>
      {STAGES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
    </select>
  );
};
const BRIEF = [["travellers", "Travellers"], ["occasion", "Occasion"], ["fixed", "Fixed dates / events"], ["interests", "Interests"], ["pace", "Pace"], ["budget", "Budget"], ["accommodation", "Accommodation"], ["must_do", "Must-dos"], ["avoid", "Avoid"], ["dietary", "Dietary / access"], ["notes", "Notes"]];

const NewRequest = ({ open, setOpen, onDone }) => {
  const [f, setF] = useState(BLANK_REQUEST);
  const save = async (e) => {
    e.preventDefault();
    try { await http.post("/bespoke/requests/admin", f); toast.success("Request added"); setF(BLANK_REQUEST); setOpen(false); onDone(); }
    catch (ex) { toast.error(errMsg(ex)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="new-request-dialog" className="bg-[#FAF9F6] text-[#2B2A26] max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New bespoke request</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-6"><RequestFields value={f} onChange={setF} />
          <button data-testid="new-request-save" className="rounded-sm bg-[#2B2A26] text-white px-6 py-3 font-bold">Save request</button></form>
      </DialogContent>
    </Dialog>
  );
};

const RequestDetail = ({ r, reload }) => {
  const send = async () => {
    try {
      const { data } = await http.post(`/bespoke/requests/${r.id}/send`);
      toast.success(data.held ? `Balance of A$${Number(data.balance).toFixed(2)} requested from ${data.to} — the kit unlocks once it's paid` : `Kit emailed to ${r.email}`); reload();
    }
    catch (e) { toast.error(errMsg(e)); }
  };
  const decline = async () => { await http.patch(`/bespoke/requests/${r.id}`, { status: r.status === "declined" ? "new" : "declined" }); reload(); };
  return (
    <div data-testid="request-detail" className="rounded-xl border border-stone-200 bg-white p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div><h2 className="font-display text-3xl">{r.name}</h2><div className="text-sm text-stone-500">{r.email || "no email yet"}{r.phone ? ` · ${r.phone}` : ""} · via {r.source}</div></div>
        <div className="flex items-center gap-3"><EditRequest r={r} reload={reload} /><DeleteRequest r={r} reload={reload} />
          <StageSelect r={r} reload={reload} /></div>
      </div>
      <div className="text-lg mb-1">{r.destination}</div>
      <div className="text-sm text-stone-500 mb-5">{r.start_date} → {r.end_date}</div>
      <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-sm mb-6">
        {BRIEF.filter(([k]) => r[k]).flatMap(([k, l]) => [<dt key={`${k}-l`} className="text-stone-400">{l}</dt>, <dd key={k} className="text-stone-700 whitespace-pre-wrap">{r[k]}</dd>])}
      </dl>
      <CustomerReplies r={r} reload={reload} />
      <ReviewPdfs r={r} reload={reload} />
      <BriefVersions r={r} />
      <div className="flex flex-wrap items-center gap-3">
        {!r.kit_id && r.status !== "declined" && <BuildDialog req={r} onBuilt={reload} />}
        {r.kit && <Link data-testid="request-open-kit" to={`/kits/${r.kit.id}`} className="inline-flex items-center gap-2 rounded-sm border border-stone-300 px-4 py-2 text-sm hover:bg-stone-50"><ExternalLink size={14} /> Open kit · {r.kit.status}{r.kit.progress?.current ? ` — ${r.kit.progress.current}` : ""}</Link>}
        {r.kit && ["review", "approved"].includes(r.kit.status) && (
          <button data-testid="request-send" onClick={send} className="inline-flex items-center gap-2 rounded-sm bg-[#2B2A26] text-white px-4 py-2 text-sm font-bold"><Send size={14} /> {r.status === "sent" ? "Send again" : `Email kit to ${r.name.split(" ")[0]}`} · ${r.price}</button>)}
        {!r.kit_id && <button data-testid="request-decline" onClick={decline} className="text-sm text-stone-500 hover:text-red-600">{r.status === "declined" ? "Reopen" : "Decline"}</button>}
      </div>
    </div>
  );
};

export default function Bespoke() {
  const [list, setList] = useState([]);
  const [params] = useSearchParams();
  const [sel, setSel] = useState(params.get("r"));
  const [open, setOpen] = useState(false);
  const load = useCallback(() => http.get("/bespoke/requests").then((r) => setList(r.data)), []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  const current = list.find((r) => r.id === sel) || list[0];
  const formUrl = `${window.location.origin}/plan-my-trip`;
  return (
    <div className="p-8 lg:p-12" data-testid="bespoke-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div><div className="eyebrow mb-2">Customer requests</div><h1 className="font-display text-4xl sm:text-5xl">Bespoke Kits</h1></div>
        <div className="flex gap-2">
          <UploadBespoke onDone={load} />
          <button data-testid="copy-form-link" onClick={() => { navigator.clipboard?.writeText(formUrl); toast.success("Plan My Trip link copied"); }} className="inline-flex items-center gap-2 rounded-sm border border-stone-300 px-4 py-2 text-sm"><Copy size={14} /> Copy Plan My Trip link</button>
          <button data-testid="new-request-button" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-sm bg-[#C9A847] text-white px-4 py-2 text-sm font-bold"><Plus size={15} /> New request</button>
        </div>
      </div>
      <PaymentSettings />
      {list.length === 0 ? <div data-testid="bespoke-empty" className="text-stone-500">No requests yet. Share the Plan My Trip link or add one yourself.</div> : (
        <div className="grid lg:grid-cols-[340px_1fr] gap-6">
          <div className="space-y-2">
            {list.map((r) => (
              <button key={r.id} data-testid={`request-item-${r.id}`} onClick={() => setSel(r.id)}
                className={`w-full text-left rounded-lg border p-4 transition-colors ${current?.id === r.id ? "border-[#C9A847] bg-white" : "border-stone-200 bg-white/60 hover:bg-white"}`}>
                <div className="flex justify-between gap-2"><span className="font-medium">{r.name}{(r.review_changes || []).some((c) => !c.done) && <span title="Changes requested" className="ml-2 inline-block w-2 h-2 rounded-full bg-[#C9A847]" />}</span><span data-testid={`request-stage-${r.id}`} className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase whitespace-nowrap ${stageOf(r)[2]}`}>{stageOf(r)[1]}</span></div>
                <div className="text-sm text-stone-500">{r.destination} · {r.start_date}</div>
              </button>
            ))}
          </div>
          {current && <RequestDetail r={current} reload={load} />}
        </div>
      )}
      <NewRequest open={open} setOpen={setOpen} onDone={load} />
    </div>
  );
}
